import React from "react";

import type { DrawerSize } from "@/components/ui/v2/use-drawer";
import VacancyDetailsDrawer from "@/features/vacancies/components/details/VacancyDetailsDrawer";
import { instance } from "@/lib/axios/instance";
import type { VacancyType } from "@/types/vacancy/VacancyType";

export type OpenDrawer = (
  content: React.ReactNode,
  title: string,
  size?: DrawerSize,
) => void;

/**
 * Renders the vacancy details drawer for a fully-loaded vacancy.
 */
export function openVacancyDrawer(
  clientId: string,
  vacancy: VacancyType,
  openDrawer: OpenDrawer,
  title = "Vacancy details",
): void {
  openDrawer(
    React.createElement(VacancyDetailsDrawer, {
      clientId,
      vacancy,
      isLoading: false,
      isFetching: false,
    }),
    title,
    "medium",
  );
}

/**
 * Fetches a vacancy by its numeric ID and opens the drawer.
 */
export async function openVacancyDrawerById(
  clientId: string,
  vacancyId: number,
  openDrawer: OpenDrawer,
  title?: string,
): Promise<void> {
  const response = await instance.get<VacancyType>(
    `/api/v2/Vacancies/${vacancyId}`,
    { params: { clientId } },
  );
  openVacancyDrawer(clientId, response.data, openDrawer, title);
}

type VacancyDrawerRowClickConfig = {
  /** Extract the identifier from the clicked row to match against vacancies. */
  getRowId: (row: unknown) => string | undefined;
  /** Custom match logic. Defaults to matching publID or vacancyID. */
  matchVacancy?: (
    vacancy: { publID: string | null; vacancyID: string | null },
    id: string,
  ) => boolean;
  drawerTitle?: string;
  onError?: () => void;
};

const defaultMatch = (
  v: { publID: string | null; vacancyID: string | null },
  id: string,
) => v.publID === id || v.vacancyID === id;

/**
 * Generic click handler that paginates through a client's vacancies,
 * matches by ID, fetches full details, and opens the vacancy drawer.
 *
 * TODO: replace the client-side pagination scan with a backend lookup endpoint
 * so this works for clients with >1 000 vacancies without multiple round trips.
 */
export function createVacancyDrawerRowClickHandler(
  clientId: string,
  openDrawer: OpenDrawer,
  config: VacancyDrawerRowClickConfig,
): (row: unknown) => Promise<void> {
  const match = config.matchVacancy ?? defaultMatch;

  return async (row: unknown) => {
    const id = config.getRowId(row);
    if (!id) return;

    try {
      let page = 1;
      const pageSize = 100;

      while (page <= 10) {
        const response = await instance.get<{
          data: Array<{
            id: number;
            publID: string | null;
            vacancyID: string | null;
          }>;
          totalRecords: number;
        }>("/api/v2/Vacancies", {
          params: { clientId, page, pageSize },
        });

        const vacancy = response.data.data.find((v) => match(v, id));

        if (vacancy) {
          await openVacancyDrawerById(
            clientId,
            vacancy.id,
            openDrawer,
            config.drawerTitle,
          );
          return;
        }

        if (
          response.data.data.length < pageSize ||
          page * pageSize >= response.data.totalRecords
        )
          break;
        page++;
      }
    } catch (error) {
      console.error("Failed to fetch vacancy details:", error);
      config.onError?.();
    }
  };
}
